import { supabase } from '../lib/supabase';
import { Result, TeamResult } from '../types/models';
import { calculateChampions } from './championService';

// Overlay the house from student_enrollments (matched on student_id +
// competition_year) so historical individual results show the house the
// student was in for that year rather than their current house.
const overlayEnrollmentHouses = async (results: Result[]): Promise<Result[]> => {
  const pairs = results
    .filter(r => r.student?.student_id && r.competition_year)
    .map(r => ({ student_id: r.student!.student_id, year: r.competition_year }));

  if (pairs.length === 0) return results;

  const studentIds = Array.from(new Set(pairs.map(p => p.student_id)));
  const years = Array.from(new Set(pairs.map(p => p.year)));

  const { data: enrollments, error } = await supabase
    .from('student_enrollments')
    .select(`
      student_id,
      competition_year,
      house:houses(house_id, house_name, house_color)
    `)
    .in('student_id', studentIds)
    .in('competition_year', years);

  if (error) {
    console.warn('Could not load enrollment houses for results:', error.message);
    return results;
  }

  const byKey = new Map<string, any>();
  (enrollments || []).forEach((e: any) => {
    byKey.set(`${e.student_id}-${e.competition_year}`, e.house);
  });

  return results.map(r => {
    if (!r.student) return r;
    const historicalHouse = byKey.get(`${r.student.student_id}-${r.competition_year}`);
    if (!historicalHouse) return r;
    return { ...r, student: { ...r.student, house: historicalHouse } };
  });
};

export const getResults = async (): Promise<Result[]> => {
  const { data, error } = await supabase
    .from('competition_results')
    .select(`
      *,
      student:students(
        student_id,
        first_name,
        last_name,
        date_of_birth,
        house:houses(
          house_id,
          house_name,
          house_color
        )
      ),
      event:events(
        *,
        category:sports_categories(*)
      )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return overlayEnrollmentHouses(data || []);
};

export const getResultsByCompetition = async (eventId: string): Promise<Result[]> => {
  // First check if this is a team event
  const { data: event, error: eventError } = await supabase
    .from('events')
    .select('is_team_event')
    .eq('event_id', eventId)
    .single();
    
  if (eventError) {
    throw new Error(eventError.message);
  }
  
  if (event.is_team_event) {
    // For team events, get team results
    const { data: teamResults, error: teamResultsError } = await supabase
      .from('team_results')
      .select(`
        result_id,
        event_id,
        house_id,
        competition_year,
        position,
        points_earned,
        created_at,
        verified_by,
        house:houses(
          house_id,
          house_name,
          house_color
        )
      `)
      .eq('event_id', eventId)
      .order('position', { ascending: true, nullsLast: true });
      
    if (teamResultsError) {
      throw new Error(teamResultsError.message);
    }
    
    // Convert team results to the Result format for UI compatibility
    const formattedResults: Result[] = (teamResults || []).map(tr => ({
      result_id: tr.result_id,
      event_id: tr.event_id,
      student_id: tr.house_id, // Use house_id as student_id for UI compatibility
      age_group_id: '', // Not applicable for team events
      competition_year: tr.competition_year,
      position: tr.position,
      points_earned: tr.points_earned,
      created_at: tr.created_at,
      verified_by: tr.verified_by,
      student: {
        student_id: tr.house_id, // Use house_id as student_id for UI compatibility
        first_name: tr.house?.house_name || 'Unknown',
        last_name: 'Team',
        date_of_birth: '',
        house: tr.house
      }
    }));
    
    return formattedResults;
  } else {
    // For individual events, get individual results
    const { data, error } = await supabase
      .from('competition_results')
      .select(`
        *,
        student:students(
          student_id,
          first_name,
          last_name,
          first_name_tamil,
          last_name_tamil,
          date_of_birth,
          house:houses(
            house_id,
            house_name,
            house_color
          )
        ),
        age_group:age_groups(*)
      `)
      .eq('event_id', eventId)
      .order('position', { ascending: true, nullsLast: true });

    if (error) {
      throw new Error(error.message);
    }

    return overlayEnrollmentHouses(data || []);
  }
};

export const getResultById = async (id: string): Promise<Result | null> => {
  const { data, error } = await supabase
    .from('competition_results')
    .select(`
      *,
      student:students(
        student_id,
        first_name,
        last_name,
        date_of_birth,
        house:houses(
          house_id,
          house_name,
          house_color
        )
      ),
      event:events(*),
      age_group:age_groups(*)
    `)
    .eq('result_id', id)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const createResult = async (result: Omit<Result, 'result_id' | 'created_at' | 'student' | 'event'>): Promise<Result> => {
  // First check if user has required role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select('roles:roles(role_name)')
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error(rolesError.message);
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles?.role_name === 'admin' || 
    ur.roles?.role_name === 'manager' ||
    ur.roles?.role_name === 'referee'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Requires admin, manager, or referee role');
  }

  const { data, error } = await supabase
    .from('competition_results')
    .insert([result])
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }

  // Recalculate champions after adding new result (with error handling)
  try {
    await calculateChampions(result.competition_year);
  } catch (championError) {
    console.warn('Failed to recalculate champions after creating result:', championError);
    // Don't throw error here as the result was successfully created
  }
  
  return data;
};

export const createTeamResult = async (result: Omit<TeamResult, 'result_id' | 'created_at' | 'house'>): Promise<TeamResult> => {
  // First check if user has required role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select('roles:roles(role_name)')
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error(rolesError.message);
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles?.role_name === 'admin' || 
    ur.roles?.role_name === 'manager' ||
    ur.roles?.role_name === 'referee'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Requires admin, manager, or referee role');
  }

  const { data, error } = await supabase
    .from('team_results')
    .insert([result])
    .select(`
      *,
      house:houses(
        house_id,
        house_name,
        house_color
      )
    `)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const createMassResults = async (results: Array<Omit<Result, 'result_id' | 'created_at' | 'student' | 'event'>>): Promise<Result[]> => {
  // First check if user has required role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select('roles:roles(role_name)')
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error(rolesError.message);
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles?.role_name === 'admin' || 
    ur.roles?.role_name === 'manager' ||
    ur.roles?.role_name === 'referee'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Requires admin, manager, or referee role');
  }

  const { data, error } = await supabase
    .from('competition_results')
    .insert(results)
    .select();
    
  if (error) {
    throw new Error(error.message);
  }

  // Recalculate champions after adding new results (with error handling)
  if (results.length > 0) {
    try {
      await calculateChampions(results[0].competition_year);
    } catch (championError) {
      console.warn('Failed to recalculate champions after creating mass results:', championError);
      // Don't throw error here as the results were successfully created
    }
  }
  
  return data || [];
};

export const createMassTeamResults = async (results: Array<Omit<TeamResult, 'result_id' | 'created_at' | 'house'>>): Promise<TeamResult[]> => {
  // First check if user has required role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select('roles:roles(role_name)')
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error(rolesError.message);
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles?.role_name === 'admin' || 
    ur.roles?.role_name === 'manager' ||
    ur.roles?.role_name === 'referee'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Requires admin, manager, or referee role');
  }

  const { data, error } = await supabase
    .from('team_results')
    .insert(results)
    .select();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const updateResult = async (id: string, result: Partial<Omit<Result, 'result_id' | 'created_at' | 'student' | 'event'>>): Promise<Result> => {
  // First check if user has required role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select('roles:roles(role_name)')
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error(rolesError.message);
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles?.role_name === 'admin' || 
    ur.roles?.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update results');
  }

  const { data, error } = await supabase
    .from('competition_results')
    .update(result)
    .eq('result_id', id)
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }

  // Recalculate champions after updating result (with error handling)
  if (data) {
    try {
      await calculateChampions(data.competition_year);
    } catch (championError) {
      console.warn('Failed to recalculate champions after updating result:', championError);
      // Don't throw error here as the result was successfully updated
    }
  }
  
  return data;
};

export const updateTeamResult = async (id: string, result: Partial<Omit<TeamResult, 'result_id' | 'created_at' | 'house'>>): Promise<TeamResult> => {
  // First check if user has required role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select('roles:roles(role_name)')
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error(rolesError.message);
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles?.role_name === 'admin' || 
    ur.roles?.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update results');
  }

  const { data, error } = await supabase
    .from('team_results')
    .update(result)
    .eq('result_id', id)
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const deleteResult = async (id: string): Promise<void> => {
  // Get the result year before deleting
  const { data: result } = await supabase
    .from('competition_results')
    .select('competition_year')
    .eq('result_id', id)
    .single();

  const { error } = await supabase
    .from('competition_results')
    .delete()
    .eq('result_id', id);
    
  if (error) {
    throw new Error(error.message);
  }

  // Recalculate champions after deleting result (with error handling)
  if (result) {
    try {
      await calculateChampions(result.competition_year);
    } catch (championError) {
      console.warn('Failed to recalculate champions after deleting result:', championError);
      // Don't throw error here as the result was successfully deleted
    }
  }
};

export const deleteTeamResult = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('team_results')
    .delete()
    .eq('result_id', id);
    
  if (error) {
    throw new Error(error.message);
  }
};